import Styles from './styles.module.css';
import React, { useState } from 'react';
import  { QuestionCard } from '../../../package/slider/index';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { currentPersonAxes, PERSON_DEF_VERSION_CURRENT } from '../personAxes';
import { searchIndexRegister } from '../navigation/publishLogic';

// 2026-10-20: 新方式(rawScore差分方式)対応。2状態(未入力/評価済み)を区別する。
// 中間(真ん中)は評価済みとして扱い、バランス型希望としてマッチ率の計算に含める(除外しない)。
// 詳細は「マッチ率新方式_検証シミュレーションレポート_2026-10-20.txt」
// 「性格診断・求める人物像・マッチ率の改修実装」参照。
type AxisState = 'unset' | 'rated';

type personAxisV2 = {
  state: AxisState;
  value?: number; // -1〜+1。state==='rated' のときだけ意味を持つ。
  left: number;   // 旧クライアント向けの表示用ミラー(0〜100)。
  right: number;
};

// v1(旧方式、{left,right}のみ)。読み込み時の判別・移行専用。
type personDataV1 = {
  isCompleted: boolean,
  version?: number,
  H_C: { left: number, right: number },
  T_E: { left: number, right: number },
  J_P: { left: number, right: number },
  S_R: { left: number, right: number },
};

type personData = {
  isCompleted: boolean,
  version?: number, // 定義のバージョン(../personAxes.ts)。無ければ 1。
  H_C: personAxisV2,
  T_E: personAxisV2,
  J_P: personAxisV2,
  S_R: personAxisV2,
};

type RecruitPerson = {
  documentId: string;
  setChangeData: React.Dispatch<React.SetStateAction<boolean>>;
  person: personData | personDataV1;
  setPerson: React.Dispatch<React.SetStateAction<any>>;
  /** 公開中(recruitStatus===1)の求人かどうか。公開中なら保存時にsearchIndexも即時再同期する。 */
  isPublished?: boolean;
};

type Axis = "H_C" | "T_E" | "J_P" | "S_R";

// 項目名・左右のラベル・説明文は、定義表(../personAxes.ts)の今のバージョンから取る。
// ラベルを変えるときは定義表を直す(学生アプリ・サロンアプリ・functions にも同じ定義表がある)。
const QUESTION_CONFIG = currentPersonAxes().map(a => ({ axis: a.key as Axis, number: a.number, labels: [a.left, a.right] }));

const DESCRIPTION_MAP: Record<string, string[]> = Object.fromEntries(
  currentPersonAxes().flatMap(a => [[a.left, a.descriptions.left], [a.right, a.descriptions.right]]),
);

const SCORE_SCALE = [100, 84, 68, 50, 32, 16, 0];

// 選択index(0〜6、0=強く左側を重視 〜 6=強く右側を重視)から作る。
// salonAxis(マッチ率計算に使うvalue) = (3-index)/3(丸め誤差を避けるため、保存済みの
// {left,right}からの逆算ではなく選択index自体から直接計算する。memorii・salon_app側と同じ式)。
//
// left/rightは「表示用ミラー」のみの役割で、計算には使わない(computeMatchV2はvalueだけを見る)。
// 2026-10-20修正: ここを 50+50*value で丸めると(例: index1→83)、共有部品QuestionCard側の
// scoreToIndex()が旧スケール[100,84,68,50,32,16,0]と「完全一致」でしか選択位置を復元できない
// ため、83≠84 で一致せず「未選択」に見える不具合があった(index0,3,6=両端と中央だけ値が
// 偶然一致するため気づきにくい)。旧スケールの値をそのまま使うことで、保存→表示の往復を
// 正しく一致させる。
const axisFromIndex = (index: number): personAxisV2 => {
  const value = (3 - index) / 3;
  const left = SCORE_SCALE[index];
  return { state: 'rated', value, left, right: 100 - left };
};
const UNSET: personAxisV2 = { state: 'unset', left: 50, right: 50 };

const isV1Shape = (p: personData | personDataV1): p is personDataV1 => {
  const anyAxis = (p as any)?.H_C;
  return anyAxis != null && anyAxis.state == null;
};

// v1(旧方式)データをv2に読み替える。H_C・T_E はそのまま近い位置に、
// J_P は左右を入れ替え、S_R は意味が違うので未入力に戻す。旧い値は保存時に履歴へ退避する。
const migrateFromV1 = (p: personDataV1): personData => {
  const indexOf = (a?: { left: number; right: number }): number | null => {
    if (!a || (a.left || 0) + (a.right || 0) <= 10) return null;
    let best = 0;
    for (let i = 1; i < SCORE_SCALE.length; i++) {
      if (Math.abs(SCORE_SCALE[i] - a.left) < Math.abs(SCORE_SCALE[best] - a.left)) best = i;
    }
    return best;
  };
  const migrate = (idx: number | null) => (idx == null ? UNSET : axisFromIndex(idx));
  const jpIdxOld = indexOf(p.J_P);
  const jpIdx = jpIdxOld == null ? null : 6 - jpIdxOld;

  return {
    isCompleted: false,
    version: 2,
    H_C: migrate(indexOf(p.H_C)),
    T_E: migrate(indexOf(p.T_E)),
    S_R: UNSET, // v1のS_Rは別概念なので引き継がない
    J_P: migrate(jpIdx),
  };
};

const isUnset = (a: personAxisV2) => a.state === 'unset';

const RecruitPerson: React.FC<RecruitPerson> = ({ documentId, setChangeData, person, setPerson, isPublished }) => {

  const [selectedQuestion, setSelectedQuestion] = useState<string>("");

  const [ banner, setBanner ] = useState<boolean>(false);
  const [ error, setError ] = useState<string>('');

  // 読み込み時に一度だけ、v1→v2の移行を判定する(以後は view==person として扱う)。
  const [ migratedFromV1 ] = useState<boolean>(() => isV1Shape(person));
  const [ originalV1 ] = useState<personDataV1 | null>(() => (isV1Shape(person) ? person : null));
  const view: personData = isV1Shape(person) ? migrateFromV1(person) : person;

  const updatePersonScore = (axis: Axis, uiIndex: number) => {
    setPerson((prev: any) => {
      const base: personData = isV1Shape(prev) ? migrateFromV1(prev) : prev;
      return { ...base, version: PERSON_DEF_VERSION_CURRENT, [axis]: axisFromIndex(uiIndex) };
    });
    setChangeData(true);
  };

  const [ uploading, setUploading ] = useState(false);

  const save = async (isCompleted: boolean) => {
    const current: personData = isV1Shape(person) ? migrateFromV1(person) : person;
    // 「保存」(完了)は、4項目すべての選択が必要。
    if (isCompleted && QUESTION_CONFIG.some(({ axis }) => isUnset(current[axis]))) {
      setError('保存するには、4つの項目をすべて選んでください。');
      return;
    }
    setError('');

    const db = getFirestore();
    setUploading(true);
    let ok = false;

    try {
      await updateDoc(doc(db, "recruits", documentId), {
        person: {
          ...current,
          version: PERSON_DEF_VERSION_CURRENT,
          isCompleted,
          time: new Date(),
        },
        // v1からの移行時だけ、旧値をpersonHistory.v1に退避する(データを消さない)。
        ...(migratedFromV1 && originalV1 ? { [`personHistory.v${originalV1.version ?? 1}`]: { ...originalV1, archivedAt: new Date() } } : {}),
      });

      // 求める人物像の単独保存でもsearchIndexを即時再同期する(公開中の求人のみ)。
      // 2026-10-20修正: 従来は求人公開等の操作のときしか再同期されず、人物像だけ変更すると
      // 一覧・ガイダンス検索のマッチ率が古いまま残っていた。
      if (isPublished) {
        const companyId = documentId.replace(/_2027$/, '');
        await searchIndexRegister(companyId);
      }

      ok = true;
      setChangeData(false);
    } catch (e) {
      console.error("🔥 保存失敗:", e);
      setError('保存に失敗しました。時間をおいてもう一度お試しください。');
    } finally {
      setUploading(false);
      // 以前は失敗しても「保存しました」と出ていた。成功したときだけ出す。
      if (ok) {
        setBanner(true);
        setTimeout(() => setBanner(false), 1500);
      }
    }
  };

  return (
    <>
    {banner && <div className={Styles.banner}>保存しました。</div>}
    {error && <p className={Styles.desc} style={{ color: '#E44E70' }}>{error}</p>}

    {uploading && (
      <div className={Styles.lockOverlay}>
        <div className={Styles.spinner} />
        <p>アップロード中...</p>
      </div>
    )}

    <div className={Styles.title_edit}>
      <h4>求める人物像</h4>
      <div className={Styles.title_edit_btn}>
        <button className={Styles.save_box_now} onClick={() => save(false)}>一時保存</button>
        <button className={Styles.save_box_save} onClick={() => save(true)}>保存</button>
      </div>
    </div>

    {migratedFromV1 && (
      <p className={Styles.desc}>項目の定義を新しくしました。内容を確認し、保存してください(「思考軸」は意味が変わったため未選択になっています)。</p>
    )}

    {selectedQuestion && (
      <div className={Styles.test_box}>
        <h4 className={Styles.desc_title}>{selectedQuestion}</h4>
        {DESCRIPTION_MAP[selectedQuestion]?.map((text, i) => (
          <p key={i} className={Styles.desc}>・{text}</p>
        ))}
      </div>
    )}

    <div className={Styles.test_box}>
      {QUESTION_CONFIG.map(({ axis, number, labels }) => {
        const a = view[axis];
        return (
          <QuestionCard
            key={axis}
            number={number}
            questions={labels}
            // 未入力は未選択として表示する。
            initialChoice={a.state === 'rated' ? a.left : undefined}
            setQuestions={setSelectedQuestion}
            onChange={(value) => updatePersonScore(axis, value)}
          />
        );
      })}
    </div>
    </>
  );
}

export default RecruitPerson;
